import { Button, ConfirmModal, FilterSearchBar, Table } from '@/components';
import { queryClient } from '@/constants';
import { CreateCompanyModal } from '@/modules/Companies/pages/CompaniesPage/components/CreateCompanyModal';
import { CompanyKeys, useDeleteCompany } from '@/modules/Companies/queries';
import { AttachEntityToInfrastructureCostModal } from '@/modules/Infrastructure/pages/InfrastructureCostPage/components/AttachEntityToInfrastructureCostModal';
import { OrganizationKeys } from '@/modules/Organizations/queries/keys';
import {
  automationsCountColumnConfig,
  businessAreasCountColumnConfig,
  deleteRowColumnConfig,
  employeesCountColumnConfig,
  nameWithAvatarColumnConfig,
  processesCountColumnConfig,
} from '@/utils';
import { ReactElement, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TabContext } from '../TabContainer';
import { TableRow } from '../Table/types';

interface CompaniesTabProps {
  userId?: number;
  canCreate?: boolean;
  canAttachUser?: boolean;
  canAttachCost?: boolean;
  costId?: string;
  organizationId?: number;
  disableFields?: string[];
}

export const CompaniesTab = ({
  canCreate,
  canAttachCost,
  costId,
  organizationId,
  disableFields,
}: CompaniesTabProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [companySelected, setCompanySelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const {
    data: companies,
    isLoading: isLoadingCompanies,
    isOpen,
    page,
    onPageChange,
    handleSearchChange,
    handleModalState,
    onSort,
  } = useContext(TabContext);

  const routeChange = (row?: TableRow) => {
    const path = `/companies/${row?.id}`;
    navigate(path);
  };

  const { mutate: deleteCompany } = useDeleteCompany();

  const handleOpenRemoveCompanyModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setCompanySelected(id);
  }, []);

  const handleCloseRemoveCompanyModalState = useCallback(() => {
    setIsOpenDelete(false);
    setCompanySelected(undefined);
  }, []);

  const handleDeleteCompany = useCallback(() => {
    if (!companySelected) {
      return;
    }

    deleteCompany(companySelected, {
      onError: () => {
        toast.error(t('error_deleting_company'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(CompanyKeys.ALL);
        queryClient.invalidateQueries(OrganizationKeys.ALL);
        toast.success(t('success_deleting_company'));
      },
    });

    handleCloseRemoveCompanyModalState();
  }, [companySelected, deleteCompany, handleCloseRemoveCompanyModalState, t]);

  const columns = [
    nameWithAvatarColumnConfig({}),
    employeesCountColumnConfig(),
    businessAreasCountColumnConfig(),
    processesCountColumnConfig(),
    automationsCountColumnConfig({
      options: {
        className: 'w-25',
      },
    }),
    deleteRowColumnConfig({
      disabledMessage: t('delete_company'),
      onDeleteClick(row) {
        handleOpenRemoveCompanyModalState(row?.id as number);
      },
    }),
  ];

  return (
    <>
      <FilterSearchBar
        total={companies?.totalRows || 0}
        title={t('companies')}
        onSearch={handleSearchChange}
        variant={'secondary'}
        otherActions={
          <>
            {canCreate && (
              <Button onClick={handleModalState} variant="primary">
                {t('add')}
              </Button>
            )}
            {canAttachCost && costId && (
              <AttachEntityToInfrastructureCostModal
                costId={parseInt(costId)}
                context="company"
              />
            )}
          </>
        }
      />
      <Table
        onRowClick={routeChange}
        columns={columns}
        rows={companies?.items ?? []}
        isLoading={isLoadingCompanies}
        noResultsLabel={t('no_companies_available')}
        withPagination
        totalItems={companies?.totalRows}
        totalPages={companies?.totalPages}
        currentPage={page}
        onSort={onSort}
        onPageChange={onPageChange}
      />
      {isOpen && (
        <CreateCompanyModal
          isOpen={isOpen}
          handleClose={handleModalState}
          organizationId={organizationId}
          disableFields={disableFields}
        />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveCompanyModalState}
        title={t('please_confirm_action')}
        message={t('confirm_company_delete')}
        onConfirm={handleDeleteCompany}
      />
    </>
  );
};
